import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import { FaArrowUp, FaArrowDown, FaImage, FaLink, FaTimes, FaPlay, FaReply } from 'react-icons/fa';
import './Master.css'; // Updated import
import GiphyLogo from "../../Assets/Images/PoweredBy_200px-White_HorizText.png";
import ForumEditDeleteControls from './ForumEditDeleteControls';

const fetchUsername = async (userId) => {
  try {
    const r = await axiosInstance.get(`/messageboard/usernames/${userId}/`);
    return r.data.username;
  } catch {
    return 'Unknown User';
  }
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return isMobile;
};

/* NEW: try to detect your fixed bottom navbar height and set --tabbar-h */
const useDetectTabbarHeight = () => {
  useEffect(() => {
    const sel = '[data-app-tabbar], .mobile-tabbar, .app-tabbar, nav[role="tablist"], footer[role="tablist"]';
    const el = document.querySelector(sel);
    if (el) {
      const setVar = () => {
        const h = Math.ceil(el.getBoundingClientRect().height || 64);
        document.documentElement.style.setProperty('--tabbar-h', `${h}px`);
      };
      setVar();
      const ro = new ResizeObserver(setVar);
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, []);
};

/* Video Player */
const VideoPlayer = ({ video }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  if (!video) return null;
  return (
    <div className="video-container">
      {!isPlaying ? (
        <div className="video-thumbnail" onClick={() => setIsPlaying(true)}>
          <img src={video.thumbnail_url} alt="Video thumbnail" />
          <div className="video-play-overlay">
            <FaPlay className="play-icon" />
            <div className="video-duration">{Math.round(video.duration)}s</div>
          </div>
        </div>
      ) : (
        <video
          controls
          autoPlay
          src={video.video_url}
          poster={video.thumbnail_url}
          style={{ width: '100%', maxHeight: '400px', borderRadius: '8px' }}
        >
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

/* Image Gallery */
const ImageGallery = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  if (!images || images.length === 0) return null;

  const openLightbox = (index) => { setCurrentImageIndex(index); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const nextImage = () => setCurrentImageIndex((p) => (p + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((p) => (p - 1 + images.length) % images.length);

  return (
    <div className="image-gallery">
      {images.length === 1 ? (
        <img
          src={images[0].image_url}
          alt="Post attachment"
          className="single-image"
          onClick={() => openLightbox(0)}
        />
      ) : (
        <div className={`image-grid ${images.length === 2 ? 'two-images' : images.length === 3 ? 'three-images' : 'four-plus-images'}`}>
          {images.slice(0, 4).map((image, index) => (
            <div key={image.id} className="image-container" onClick={() => openLightbox(index)}>
              <img src={image.image_url} alt={`Post attachment ${index + 1}`} />
              {index === 3 && images.length > 4 && (
                <div className="more-images-overlay">+{images.length - 4}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}><FaTimes /></button>
            <img src={images[currentImageIndex].image_url} alt="Enlarged view" />
            {images.length > 1 && (
              <>
                <button className="lightbox-nav prev" onClick={prevImage}>←</button>
                <button className="lightbox-nav next" onClick={nextImage}>→</button>
                <div className="lightbox-counter">{currentImageIndex + 1} / {images.length}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* Reply (threaded) */
const Reply = ({
  reply,
  onVote,
  handleReplyEdit,
  handleReplyDelete,
  loggedInUserId,
  onReplyToReply,
  depth = 0,
  maxDepth = 3,
  currentUser
}) => {
  const isMobile = useIsMobile();
  const [username, setUsername] = useState(reply.username || 'Unknown User');
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyImages, setReplyImages] = useState([]);
  const [replyGifUrl, setReplyGifUrl] = useState('');
  const [replyLinkUrl, setReplyLinkUrl] = useState('');
  const [showReplyLinkInput, setShowReplyLinkInput] = useState(false);
  const [showReplyGifSearch, setShowReplyGifSearch] = useState(false);
  const [giphyResults, setGiphyResults] = useState([]);
  const [giphyQuery, setGiphyQuery] = useState('');
  const [showNestedReplies, setShowNestedReplies] = useState(depth < 1);
  const [branchOpen, setBranchOpen] = useState(false);

  useEffect(() => {
    if (!reply.username && reply.user) {
      fetchUsername(reply.user).then(setUsername);
    }
  }, [reply.username, reply.user]);

  const handleImageUpload = (e) => setReplyImages((prev) => [...prev, ...Array.from(e.target.files)]);
  const removeReplyImage = (i) => setReplyImages((prev) => prev.filter((_, idx) => idx !== i));

  const handleSearchGiphy = async (query) => {
    if (!query.trim()) { setGiphyResults([]); return; }
    try {
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/search?api_key=EllgnXD7w2MrtrFkBep20b9jxMLpkraw&q=${encodeURIComponent(query)}&limit=8`
      );
      const data = await response.json();
      setGiphyResults(data.data || []);
    } catch {
      setGiphyResults([]);
    }
  };
  const handleSelectGif = (url) => { setReplyGifUrl(url); setGiphyResults([]); setGiphyQuery(''); setShowReplyGifSearch(false); };

  const validateUrl = (url) => { try { new URL(url); return true; } catch { return false; } };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() && !replyGifUrl && replyImages.length === 0 && !replyLinkUrl) return alert('Please add some content to your reply');
    if (replyLinkUrl && !validateUrl(replyLinkUrl)) return alert('Please enter a valid URL (including http:// or https://)');
    try {
      await onReplyToReply(reply.id, { content: replyContent, images: replyImages, gifUrl: replyGifUrl, linkUrl: replyLinkUrl });
      setReplyContent(''); setReplyImages([]); setReplyGifUrl(''); setReplyLinkUrl(''); setIsReplying(false);
      setShowReplyLinkInput(false); setShowReplyGifSearch(false); setGiphyQuery(''); setGiphyResults([]);
    } catch (err) { console.error('Error submitting reply:', err); alert('Error submitting reply. Please try again.'); }
  };

  const onToggleChildren = () => {
    if (isMobile && depth >= 1 && reply.replies?.length) setBranchOpen(true);
    else setShowNestedReplies(!showNestedReplies);
  };

  return (
    <div className={`reply depth-${depth} ${isReplying ? 'is-replying' : ''}`}>
      <div className="reply-content">
        <div className="reply-header">
          <Link to={`/memberprofile/${username}`} className="username-link"><strong>{username}</strong></Link>
          <span className="reply-date">• {new Date(reply.created_at).toLocaleDateString()}</span>
        </div>

        {reply.content && <p className="reply-text">{reply.content}</p>}

        {reply.images?.length > 0 && (
          <div className="reply-images"><ImageGallery images={reply.images} /></div>
        )}

        {reply.gif_url && (
          <div className="reply-gif-container"><img src={reply.gif_url} alt="Reply GIF" className="reply-gif" /></div>
        )}

        {reply.link_url && (
          <div className="post-link">
            <FaLink className="link-icon" />
            <a href={reply.link_url} target="_blank" rel="noopener noreferrer">{reply.link_url}</a>
          </div>
        )}

        <div className="reply-actions">
          <div className="vote-buttons">
            <FaArrowUp onClick={() => onVote(reply.id, 'upvote', true)} />
            <span>{reply.upvotes}</span>
            <FaArrowDown onClick={() => onVote(reply.id, 'downvote', true)} />
            <span>{reply.downvotes}</span>
          </div>

          {depth < maxDepth && (
            <button onClick={() => setIsReplying(!isReplying)} className="reply-button">
              <FaReply /> Reply
            </button>
          )}

          <ForumEditDeleteControls
            messageId={reply.id}
            isReply={true}
            originalContent={reply.content}
            onEdit={handleReplyEdit}
            onDelete={handleReplyDelete}
            isOwner={Number(reply.user) === Number(loggedInUserId)}
          />
        </div>

        {isReplying && (
          <div className="nested-reply-form">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder={`Replying to ${username}...`}
              className="nested-reply-textarea"
            />
            <div className="nested-reply-controls">
              <label className="media-button-small">
                <FaImage />
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="visually-hidden" />
              </label>
              <button
                type="button"
                onClick={() => setShowReplyGifSearch(!showReplyGifSearch)}
                className={`media-button-small ${showReplyGifSearch ? 'active' : ''}`}
              >
                🎬
              </button>
              {!showReplyLinkInput && (
                <button type="button" onClick={() => setShowReplyLinkInput(true)} className="media-button-small">
                  <FaLink />
                </button>
              )}
            </div>

            {showReplyLinkInput && (
              <div className="nested-link-input">
                <input type="url" placeholder="https://example.com" value={replyLinkUrl} onChange={(e) => setReplyLinkUrl(e.target.value)} />
                <button onClick={() => { setShowReplyLinkInput(false); setReplyLinkUrl(''); }}><FaTimes /></button>
              </div>
            )}

            {showReplyGifSearch && (
              <div className="nested-gif-search">
                <input
                  type="text"
                  placeholder="Search GIFs..."
                  value={giphyQuery}
                  onChange={(e) => { setGiphyQuery(e.target.value); handleSearchGiphy(e.target.value); }}
                />
                <div className="nested-giphy-results">
                  {giphyResults.map((gif) => (
                    <img
                      key={gif.id}
                      src={gif.images.fixed_height_small.url}
                      alt="GIF option"
                      onClick={() => handleSelectGif(gif.images.fixed_height.url)}
                      className="nested-giphy-result"
                    />
                  ))}
                </div>
              </div>
            )}

            {replyImages.length > 0 && (
              <div className="nested-image-previews">
                {replyImages.map((image, index) => (
                  <div key={index} className="nested-image-preview">
                    <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                    <button onClick={() => removeReplyImage(index)}><FaTimes /></button>
                  </div>
                ))}
              </div>
            )}

            {replyGifUrl && (
              <div className="nested-gif-preview">
                <img src={replyGifUrl} alt="Selected GIF" />
                <button onClick={() => setReplyGifUrl('')}><FaTimes /></button>
              </div>
            )}

            <div className="nested-reply-actions">
              <button onClick={() => { setIsReplying(false); }} className="cancel-reply-btn">Cancel</button>
              <button onClick={handleReplySubmit} className="submit-reply-btn">Post Reply</button>
            </div>
          </div>
        )}
      </div>

      {/* Nested replies */}
      {reply.replies && reply.replies.length > 0 && (
        <div className="nested-replies">
          {depth >= 1 && (
            <div className="nested-replies-toggle">
              <button onClick={onToggleChildren} className="toggle-nested-replies">
                {showNestedReplies ? `Hide ${reply.replies.length} repl${reply.replies.length > 1 ? 'ies' : 'y'}` : `Show ${reply.replies.length} repl${reply.replies.length > 1 ? 'ies' : 'y'}`}
              </button>
            </div>
          )}

          {showNestedReplies && (!isMobile || depth === 0) && reply.replies.map((nestedReply) => (
            <Reply
              key={nestedReply.id}
              reply={nestedReply}
              onVote={onVote}
              handleReplyEdit={handleReplyEdit}
              handleReplyDelete={handleReplyDelete}
              loggedInUserId={loggedInUserId}
              onReplyToReply={onReplyToReply}
              depth={depth + 1}
              maxDepth={maxDepth}
              currentUser={currentUser}
            />
          ))}

          {/* Branch Drawer (mobile, depth>=1) */}
          {branchOpen && isMobile && (
            <>
              <div className="branch-drawer-backdrop" onClick={() => setBranchOpen(false)} />
              <div className="branch-drawer is-open" role="dialog" aria-modal="true">
                <div className="branch-drawer__bar">
                  <button className="branch-drawer__back" onClick={() => setBranchOpen(false)}>← Back</button>
                  <div className="branch-drawer__title">Replies to {username}</div>
                  <div className="branch-drawer__meta">{reply.replies.length} repl{reply.replies.length > 1 ? 'ies' : 'y'}</div>
                </div>
                <div className="branch-drawer__content">
                  {reply.replies.map((child) => (
                    <Reply
                      key={child.id}
                      reply={child}
                      onVote={onVote}
                      handleReplyEdit={handleReplyEdit}
                      handleReplyDelete={handleReplyDelete}
                      loggedInUserId={currentUser?.id}
                      onReplyToReply={onReplyToReply}
                      depth={0} /* re-root inside drawer */
                      maxDepth={maxDepth}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* Main */
const ForumPostDetail = () => {
  const { id: postId } = useParams();
  const isMobile = useIsMobile();
  useDetectTabbarHeight(); // <-- set --tabbar-h automatically

  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [gifUrl, setGifUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [replyImages, setReplyImages] = useState([]);
  const [giphyResults, setGiphyResults] = useState([]);
  const [giphyQuery, setGiphyQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('Unknown User');
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showGifSearch, setShowGifSearch] = useState(false);
  const [isReplying, setIsReplying] = useState(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axiosInstance.get('/auth/current-user/');
        setCurrentUser(response.data); // Store the full user object
        setLoggedInUserId(response.data.id);
        console.log('Current user:', response.data); // Debug log
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axiosInstance.get(`/messageboard/forums/post/${postId}/`);
        const postData = response.data;
        console.log('Post data:', postData); // Debug log
        console.log('Post user ID:', postData.user); // Debug log

        if (postData.user && !postData.username) {
          const fetchedUsername = await fetchUsername(postData.user);
          postData.username = fetchedUsername;
          setUsername(fetchedUsername);
        } else {
          setUsername(postData.username || 'Unknown User');
        }
        setPost(postData);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Unable to load post details.');
      } finally {
        setLoading(false);
      }
    };

    const fetchReplies = async () => {
      try {
        const response = await axiosInstance.get(`/messageboard/forums/post/${postId}/replies/`);
        setReplies(response.data);
      } catch (err) {
        console.error('Error fetching replies:', err);
      }
    };

    fetchPost();
    fetchReplies();
  }, [postId]);

  // Debug log to check ownership
  useEffect(() => {
    if (post && loggedInUserId) {
      console.log('Ownership check:');
      console.log('  Post user ID:', post.user, typeof post.user);
      console.log('  Logged in user ID:', loggedInUserId, typeof loggedInUserId);
      console.log('  Are they equal?:', post.user === loggedInUserId);
      console.log('  Are they equal (string comparison)?:', String(post.user) === String(loggedInUserId));
    }
  }, [post, loggedInUserId]);

  const handleImageUpload = (e) => setReplyImages((prev) => [...prev, ...Array.from(e.target.files)]);
  const removeReplyImage = (i) => setReplyImages((prev) => prev.filter((_, idx) => idx !== i));
  const validateUrl = (url) => { try { new URL(url); return true; } catch { return false; } };

  const handleSearchGiphy = async (query) => {
    if (!query.trim()) { setGiphyResults([]); return; }
    try {
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/search?api_key=EllgnXD7w2MrtrFkBep20b9jxMLpkraw&q=${encodeURIComponent(query)}&limit=10`
      );
      const data = await response.json();
      setGiphyResults(data.data || []);
    } catch { setGiphyResults([]); }
  };
  const handleSelectGif = (url) => { setGifUrl(url); setGiphyResults([]); setGiphyQuery(''); setShowGifSearch(false); };
  const removeGif = () => setGifUrl('');

  const handleVote = async (id, type, isReply = false) => {
    try {
      let response;
      if (isReply) response = await axiosInstance.post(`messageboard/forums/${id}/${type}/`);
      else response = await axiosInstance.post(`/messageboard/forums/post/${post.id}/vote/`, { type });

      if (isReply) {
        const updateReplyVotes = (list) => list.map((r) => {
          if (r.id === id) return { ...r, upvotes: response.data.upvotes, downvotes: response.data.downvotes };
          if (r.replies?.length) return { ...r, replies: updateReplyVotes(r.replies) };
          return r;
        });
        setReplies(updateReplyVotes(replies));
      } else {
        setPost((prev) => ({ ...prev, upvotes: response.data.upvotes, downvotes: response.data.downvotes }));
      }
    } catch (err) { console.error(`Error ${type}voting:`, err); }
  };

  // Submit top-level reply
  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!newReply.trim() && !gifUrl && replyImages.length === 0 && !linkUrl) return alert('Please add some content to your reply');
    if (linkUrl && !validateUrl(linkUrl)) return alert('Please enter a valid URL (including http:// or https://)');

    try {
      if (!loggedInUserId) { console.error('User not logged in.'); return; }

      const formData = new FormData();
      formData.append('content', newReply);
      formData.append('user', loggedInUserId);
      formData.append('post', postId);
      if (gifUrl) formData.append('gif_url', gifUrl);
      if (linkUrl) formData.append('link_url', linkUrl);
      replyImages.forEach((image) => formData.append('images', image, image.name));

      await axiosInstance.post(`/messageboard/forums/post/${postId}/replies/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const repliesResponse = await axiosInstance.get(`/messageboard/forums/post/${postId}/replies/`);
      setReplies(repliesResponse.data);

      // Reset form
      setNewReply(''); setGifUrl(''); setLinkUrl(''); setReplyImages([]);
      setShowLinkInput(false); setShowGifSearch(false); setGiphyQuery(''); setGiphyResults([]);
      setIsReplying(false);
    } catch (error) {
      console.error("Error submitting reply:", error);
      if (error.response?.data?.error) alert(`Error: ${error.response.data.error}`);
      else alert('An error occurred while submitting your reply');
    }
  };

  // Submit nested reply
  const handleReplyToReply = async (parentId, replyData) => {
    const { content, images, gifUrl: _gifUrl, linkUrl: _linkUrl } = replyData;

    if (!content.trim() && !_gifUrl && images.length === 0 && !_linkUrl) return alert('Please add some content to your reply');
    if (_linkUrl && !validateUrl(_linkUrl)) return alert('Please enter a valid URL (including http:// or https://)');

    try {
      if (!loggedInUserId) { console.error('User not logged in.'); return; }

      const formData = new FormData();
      formData.append('content', content);
      formData.append('user', loggedInUserId);
      formData.append('post', postId);
      formData.append('parent', parentId);
      if (_gifUrl) formData.append('gif_url', _gifUrl);
      if (_linkUrl) formData.append('link_url', _linkUrl);
      images.forEach((image) => formData.append('images', image, image.name));

      await axiosInstance.post(`/messageboard/forums/post/${postId}/replies/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const repliesResponse = await axiosInstance.get(`/messageboard/forums/post/${postId}/replies/`);
      setReplies(repliesResponse.data);
    } catch (error) {
      console.error("Error submitting reply:", error);
      if (error.response?.data?.error) alert(`Error: ${error.response.data.error}`);
      else alert('An error occurred while submitting your reply');
      throw error;
    }
  };

  const handlePostEdit = (id, newTitle, newContent) => {
    setPost((prev) => ({ ...prev, title: newTitle || prev.title, content: newContent || prev.content }));
  };

  const handlePostDelete = async () => {
    setPost(null);
    // You might want to redirect to the forum list or show a message
    // window.location.href = '/forums'; // or use navigate from react-router-dom
  };

  const handleReplyEdit = (id, _newTitle, newContent) => {
    const update = (list) => list.map((r) => {
      if (r.id === id) return { ...r, content: newContent };
      if (r.replies?.length) return { ...r, replies: update(r.replies) };
      return r;
    });
    setReplies(update(replies));
  };

  const handleReplyDelete = (replyId) => {
    const remove = (list) => list.filter((r) => {
      if (r.id === replyId) return false;
      if (r.replies?.length) r.replies = remove(r.replies);
      return true;
    });
    setReplies(remove(replies));
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!post) return <div className="error">Post not found.</div>;

  // Convert to numbers to ensure proper comparison - use the same logic as MessageBoard
  const isPostOwner = currentUser && (post.user === currentUser.username || post.user === currentUser.id);

  return (
    <div className="forums-container">
      <div className="messageboard-content">
        <div className={`post-container ${isReplying ? 'is-replying' : ''}`}>
          <div className="post-meta">
            <Link to={`/memberprofile/${username}`} className="username-link"><strong>{username}</strong></Link>
            <span className="reply-date">• {new Date(post.created_at).toLocaleDateString()}</span>
          </div>
          <h2 className="post-title">{post.title}</h2>
          <p className="reply-text">{post.content}</p>

          <div className="post-media-container">
            {post.video ? <VideoPlayer video={post.video} /> :
             (post.images && post.images.length > 0 ? <ImageGallery images={post.images} /> : null)}
          </div>

          {post.link_url && (
            <div className="post-link">
              <FaLink className="link-icon" />
              <a href={post.link_url} target="_blank" rel="noopener noreferrer">{post.link_url}</a>
            </div>
          )}

          <div className="reply-actions">
            <div className="vote-buttons">
              <FaArrowUp onClick={() => handleVote(post.id, 'upvote')}/>
              <span>{post.upvotes}</span>
              <FaArrowDown onClick={() => handleVote(post.id, 'downvote')}/>
              <span>{post.downvotes}</span>
            </div>

            <button onClick={() => setIsReplying(!isReplying)} className="reply-button">
              <FaReply /> Reply
            </button>

            {/* Single Edit/Delete Controls Component */}
            <ForumEditDeleteControls
              messageId={post.id}
              isReply={false}
              originalTitle={post.title}
              originalContent={post.content}
              onEdit={handlePostEdit}
              onDelete={handlePostDelete}
              isOwner={isPostOwner}
            />

          </div>

          {isReplying && (
            <div className="nested-reply-form">
              <textarea
                value={newReply}
                onChange={(e) => setNewReply(e.target.value)}
                placeholder="Write your reply..."
                className="nested-reply-textarea"
              />
              <div className="nested-reply-controls">
                <label className="media-button-small">
                  <FaImage />
                  <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="visually-hidden" />
                </label>
                <button
                  type="button"
                  onClick={() => setShowGifSearch(!showGifSearch)}
                  className={`media-button-small ${showGifSearch ? 'active' : ''}`}
                >
                  🎬
                </button>
                {!showLinkInput && (
                  <button type="button" onClick={() => setShowLinkInput(true)} className="media-button-small">
                    <FaLink />
                  </button>
                )}
              </div>

              {showLinkInput && (
                <div className="nested-link-input">
                  <input type="url" placeholder="https://example.com" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
                  <button onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}><FaTimes /></button>
                </div>
              )}

              {showGifSearch && (
                <div className="nested-gif-search">
                  <input
                    type="text"
                    placeholder="Search GIFs..."
                    value={giphyQuery}
                    onChange={(e) => { setGiphyQuery(e.target.value); handleSearchGiphy(e.target.value); }}
                  />
                  <div className="nested-giphy-results">
                    {giphyResults.map((gif) => (
                      <img
                        key={gif.id}
                        src={gif.images.fixed_height_small.url}
                        alt="GIF option"
                        onClick={() => handleSelectGif(gif.images.fixed_height.url)}
                        className="nested-giphy-result"
                      />
                    ))}
                  </div>
                </div>
              )}

              {replyImages.length > 0 && (
                <div className="nested-image-previews">
                  {replyImages.map((image, index) => (
                    <div key={index} className="nested-image-preview">
                      <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                      <button onClick={() => removeReplyImage(index)}><FaTimes /></button>
                    </div>
                  ))}
                </div>
              )}

              {gifUrl && (
                <div className="nested-gif-preview">
                  <img src={gifUrl} alt="Selected GIF" />
                  <button onClick={() => setGifUrl('')}><FaTimes /></button>
                </div>
              )}

              <div className="nested-reply-actions">
                <button onClick={() => { setIsReplying(false); }} className="cancel-reply-btn">Cancel</button>
                <button onClick={handleReplySubmit} className="submit-reply-btn">Post Reply</button>
              </div>
            </div>
          )}
        </div>

        <div className="replies-container">
          <h3>Replies</h3>
          {replies.length > 0 ? (
            replies
              .filter(r => !r.parent)
              .map((reply) => (
                <Reply
                  key={reply.id}
                  reply={reply}
                  onVote={handleVote}
                  handleReplyEdit={handleReplyEdit}
                  handleReplyDelete={handleReplyDelete}
                  loggedInUserId={loggedInUserId}
                  onReplyToReply={handleReplyToReply}
                  depth={0}
                  maxDepth={3}
                />
              ))
          ) : (
            <p>No replies yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForumPostDetail;